// THE PLANNER — the deliverables section's flap-out agent. The benches refine ONE
// deliverable each; this chat's subject is the PLAN: whether the deliverables together cover
// every requirement, whether two of them carry the same work twice, whether the sequencing
// holds against the dependency links and dates, and what has been forgotten entirely. It
// rides the shared flyout shell (components/AgentFlyout.tsx) fixed to the right edge of the
// screen, and it is mounted at the TAB level (RegisterPage), not inside the roadmap view —
// the planner is about the plan, and the plan exists on the List sub-view too. When the
// roadmap's dock of benches is open, the board publishes --planner-bottom on :root so the
// drawer's foot clears it (RoadmapView.tsx); everywhere else the CSS fallback applies.
//
// Its runs carry context_ref 'ROADMAP' — a marker that is deliberately NOT a deliverable
// (routes/agent.ts): the queue predicate then treats EVERY proposal the thread files as
// landing outside its context, so a planning conversation fills the review queue the way a
// deliberate sweep does — each proposal reaches the queue (and, where it names a deliverable
// already on the board, that bench's Untreated list too), AND keeps a Hold on its thread card
// (the accept route's pending-only guard makes the second attempt a polite 409). P1 exactly
// as everywhere else: ◇ said by the machine, committed only by a person.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { agentApi, type ConversationResult, type RunRow, type TranscriptMessage, type TurnActivity } from '../../api/agent.js';
import AgentFlyout, { type FlyoutMsg } from '../../components/AgentFlyout.js';
import type { Deliverable, RegisterRow, TetherQuestion } from '../../api/register.js';
import { proposalDeliverableRef, proposalSubject } from '../../../shared/proposalSubject.js';
import { HOLDABLE_IN_THREAD } from './threadHold.js';
import { useToast } from '../../components/Toast.js';
import { useLocale } from '../../i18n/index.js';
import './PlannerChat.de.js';
import './PlannerChat.fr.js';

/** The run's marker — matches routes/agent.ts's literal; never a real deliverable ref. */
const ROADMAP_CONTEXT = 'ROADMAP';

type ChatProposal = ConversationResult['proposals'][number];
interface ChatMsg { who: 'op' | 'agent'; text: string; proposals?: ChatProposal[] }
/** The planner's thread survives leaving the roadmap for the life of the SPA — per engagement. */
const plannerCache = new Map<number, { msgs: ChatMsg[]; runId: number | null }>();
/** The fold state — its own key, so the planner's choice never fights the benches' (CHAT_KEY). */
const FOLD_KEY = 'signet.rw.planner';
const readFoldOpen = (): boolean => {
  try { return localStorage.getItem(FOLD_KEY) === '1'; } catch { return false; }   // private mode
};

const squash = (s: string | null | undefined): string => (s ?? '').replace(/\s+/g, ' ').trim();
/** These two mirror RoadmapView.tsx's module twins — three lines each, not worth a module. */
const transcriptText = (m: TranscriptMessage): string =>
  (m.parts ?? []).map((p) => String((p as { text?: unknown }).text ?? '')).filter(Boolean).join('\n');
const operatorWords = (raw: string): string => {
  const i = raw.lastIndexOf('Operator: ');
  return (i >= 0 ? raw.slice(i + 'Operator: '.length) : raw).trim();
};

/** One deliverable, one line — what the agent must see to judge dates, coverage and edges. */
const roadmapLines = (dels: Deliverable[]): string[] => {
  const line = (d: Deliverable): string => {
    const dates = d.due_date ? `${d.start_date ?? '…'} → ${d.due_date}` : 'UNDATED';
    // The payload carries EVERY override row of the entity — the standing mark rides beside
    // the date assumptions (RoadmapView's standingOf reads the same array), so filter by
    // field before calling anything a pencilled date.
    const pencil = (d.overrides ?? [])
      .filter((o) => o.field === 'start_date' || o.field === 'due_date')
      .map((o) => `${o.field === 'start_date' ? 'start' : 'due'} ~${o.value}`)
      .join(', ');
    const provisional = (d.overrides ?? []).some((o) => (o.field as string) === 'standing');
    const reqs = d.requirements.map((r) => r.ref).join(', ') || 'nothing';
    const builds = (d.links ?? [])
      .map((l) => `${l.to_item_ref ?? l.to_ref}${l.stale ? ' (STALE — not reconciled)' : ''}`)
      .join(', ');
    return `- ${d.ref} “${squash(d.name)}” · ${dates}${pencil ? ` · assumption pencilled: ${pencil}` : ''}`
      + `${provisional ? ' · standing PROVISIONAL' : ''}`
      + ` · satisfies ${reqs}${builds ? ` · builds on ${builds}` : ''}`
      + `${squash(d.definition_of_done) ? '' : ' · definition of done MISSING'}`;
  };
  const dated = [...dels].filter((d) => d.due_date)
    .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
  const undated = dels.filter((d) => !d.due_date);
  return [...dated, ...undated].map(line);
};

export function PlannerChat({ engagementId, dels, rows, openQuestions, onChanged, onProposalsFiled }: {
  engagementId: number;
  dels: Deliverable[];
  /** The full register — the uncovered-requirements roster is computed from its own coverage. */
  rows: RegisterRow[];
  openQuestions: TetherQuestion[];
  /** A proposal was held here: the page re-reads deliverables, standings and the ◇ queue. */
  onChanged: () => void;
  /** A turn filed proposals: the page re-reads the ◇ queue, so the cards mark up at once. */
  onProposalsFiled: () => void;
}) {
  const toast = useToast();
  const { t, fmtDate } = useLocale();
  const [open, setOpen] = useState<boolean>(readFoldOpen);
  const setFold = useCallback((v: boolean) => {
    setOpen(v);
    try { localStorage.setItem(FOLD_KEY, v ? '1' : '0'); } catch { /* private mode */ }
  }, []);

  const cached = plannerCache.get(engagementId);
  const [msgs, setMsgs] = useState<ChatMsg[]>(cached?.msgs ?? []);
  const [runId, setRunId] = useState<number | null>(cached?.runId ?? null);
  /** The runId as of NOW, for cache writes from async handlers — a closure's copy can be a
   *  click old (hold resolving after "+ New conversation" would re-stamp the dead run). */
  const runIdRef = useRef<number | null>(cached?.runId ?? null);
  const setRun = useCallback((rid: number | null) => { runIdRef.current = rid; setRunId(rid); }, []);
  const [ask, setAsk] = useState('');
  const [busy, setBusy] = useState(false);
  /** Set when this mount did not start the turn itself — the reply lives only in the record,
   *  so the poll below reloads the thread from it once the run goes quiet. */
  const [watching, setWatching] = useState(false);
  /** True while THIS mount's blocking POST is in flight — the re-attach probe must never
   *  mistake an owned turn for an orphaned one (its resolve can land after send() started). */
  const ownedTurn = useRef(false);
  /** Set by "+ New conversation": the probe's run DISCOVERY stands down, or it would find the
   *  just-dismissed run still answering and re-attach the thread the operator walked away from. */
  const freshStart = useRef(false);
  const [turnAct, setTurnAct] = useState<TurnActivity | null>(null);
  const [askStartedAt, setAskStartedAt] = useState(0);
  const [footSignal, setFootSignal] = useState(0);
  const [convos, setConvos] = useState<RunRow[] | null>(null);
  const [convosOpen, setConvosOpen] = useState(false);
  /** Set when an older thread was reopened — the next ask re-primes the planning context. */
  const [resumed, setResumed] = useState(false);

  const setBoth = useCallback((updater: (c: ChatMsg[]) => ChatMsg[], rid?: number | null) => {
    if (rid !== undefined) runIdRef.current = rid;
    setMsgs((c) => {
      const next = updater(c);
      plannerCache.set(engagementId, { msgs: next, runId: runIdRef.current });
      return next;
    });
    if (rid !== undefined) setRunId(rid);
  }, [engagementId]);

  const isPlanner = useCallback(
    (r: RunRow) => r.context_ref === ROADMAP_CONTEXT || (r.prompt ?? '').startsWith('[Roadmap planning'),
    [],
  );
  const loadConvos = useCallback(async () => {
    try {
      const out = await agentApi.listRuns(engagementId);
      const mine = out.runs.filter((r) => r.mode === 'conversation' && isPlanner(r));
      setConvos(mine);
      return mine;
    } catch { setConvos([]); return [] as RunRow[]; }
  }, [engagementId, isPlanner]);
  /** The run's thread as the RECORD has it — openConversation and the orphan-settle both read here. */
  const readThread = useCallback(async (rid: number): Promise<ChatMsg[]> => {
    const detail = await agentApi.getRun(rid);
    const thread: ChatMsg[] = [];
    for (const m of detail.transcript ?? []) {
      const txt = transcriptText(m);
      if (!txt.trim()) continue;
      if (m.role === 'user') thread.push({ who: 'op', text: operatorWords(txt) });
      else thread.push({ who: 'agent', text: txt });
    }
    // The proposals come back with the thread, riding the LAST agent turn — run-level, the
    // same recovery the bench does (RoadmapView.tsx openConversation has the full why).
    const filed: ChatProposal[] = (detail.proposals ?? []).map((pr) => ({
      id: pr.id, kind: pr.kind as ChatProposal['kind'], status: pr.status, payload: pr.payload,
    }));
    if (filed.length) {
      let last = -1;
      thread.forEach((m, i) => { if (m.who === 'agent') last = i; });
      if (last >= 0) thread[last] = { ...thread[last], proposals: filed };
      else thread.push({ who: 'agent', text: '', proposals: filed });
    }
    return thread;
  }, []);
  const openConversation = useCallback(async (run: RunRow) => {
    try {
      const thread = await readThread(run.id);
      freshStart.current = false;   // an explicitly reopened thread may re-attach again
      setMsgs(thread);
      setRun(run.id);
      setResumed(true);
      setFootSignal((n) => n + 1);
      setConvosOpen(false);
      plannerCache.set(engagementId, { msgs: thread, runId: run.id });
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The transcript did not load.')); }
  }, [engagementId, readThread, setRun, toast, t]);
  // Nothing cached? The latest planning thread comes back from the record — a reload loses nothing.
  useEffect(() => {
    void loadConvos().then((cs) => {
      if (plannerCache.get(engagementId)?.msgs.length) return;
      if (cs.length) void openConversation(cs[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);
  const newConversation = useCallback(() => {
    setMsgs([]); setRun(null); setResumed(false); setConvosOpen(false);
    // Watching an ORPHANED turn ends here — otherwise the settle below would refill the
    // cleared thread with the old conversation a few seconds later, and the probe's discovery
    // would re-attach it. An OWNED turn keeps its busy flag: its blocking POST is still out.
    freshStart.current = true;
    setWatching(false);
    if (!ownedTurn.current) setBusy(false);
    plannerCache.set(engagementId, { msgs: [], runId: null });
  }, [engagementId, setRun]);
  /** Escape closes the conversation list, like every other transient layer in the console. */
  useEffect(() => {
    if (!convosOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setConvosOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [convosOpen]);

  // A thread restored while its turn is STILL RUNNING re-attaches to it: the answering band
  // comes back (anchored to the turn's own clock), the composer stays closed, and the poll
  // below reloads the reply from the record when the run goes quiet — closing the surface
  // never loses the "answering" state, only the component that happened to be showing it.
  // A FIRST turn has no runId yet (it is born inside the blocking POST), so the probe also
  // discovers a live run by the planner's own context, and pins it once found.
  useEffect(() => {
    if (busy) return;
    let live = true;
    const probe = async () => {
      let rid = runId;
      if (rid === null) {
        if (freshStart.current) return;
        try {
          const out = await agentApi.listRuns(engagementId, 10);
          const mine = out.runs.find((r) => r.live && r.mode === 'conversation' && isPlanner(r));
          if (mine) rid = Number(mine.id);
        } catch { return; }
      }
      if (rid === null) return;
      try {
        const a = await agentApi.runActivity(rid);
        if (!live || !a.busy || ownedTurn.current) return;
        setRun(rid);   // pinned — the settle below must not depend on re-discovery succeeding
        setBusy(true);
        setWatching(true);
        setAskStartedAt(a.since ?? Date.now());
        setTurnAct(a);
      } catch { /* quiet — a finished run answers {busy:false} anyway */ }
    };
    void probe();
    return () => { live = false; };
  }, [runId, busy, engagementId, isPlanner, setRun]);

  /** The thread as rendered NOW — the settle below compares the record against it. */
  const msgsRef = useRef(msgs);
  useEffect(() => { msgsRef.current = msgs; });
  /** Settle attempts after the orphaned run went quiet — see the race note in the poll. */
  const settleTries = useRef(0);

  // While the (blocking) turn is in flight, poll what the agent is DOING — the first turn of a
  // fresh thread discovers its run by the planner's own context (the bench's exact pattern).
  useEffect(() => {
    if (!busy) { setTurnAct(null); return; }
    let live = true;
    const poll = async () => {
      let rid = runId;
      if (rid === null) {
        try {
          const out = await agentApi.listRuns(engagementId, 10);
          const mine = out.runs.find((r) => r.live && r.mode === 'conversation' && isPlanner(r));
          if (mine) rid = Number(mine.id);
        } catch { /* the clock keeps ticking */ }
      }
      if (rid !== null) {
        try {
          const a = await agentApi.runActivity(rid);
          if (!live) return;
          setTurnAct(a.busy ? a : null);
          // Re-attached and the turn just ended: the blocking POST belonged to an unmounted
          // component, so the reply exists only in the record. The record is written a beat
          // AFTER the run goes quiet (busy clears before recordTranscript commits), so accept
          // the reload only once the transcript has actually ADVANCED past what is on screen
          // — otherwise keep watching and try again next tick, up to a bounded patience.
          if (!a.busy && watching) {
            const thread = await readThread(rid);
            if (!live) return;
            const settled = thread.length >= msgsRef.current.length && thread[thread.length - 1]?.who === 'agent';
            if (settled || settleTries.current >= 8) {
              settleTries.current = 0;
              setWatching(false);
              setBusy(false);
              setBoth(() => thread, rid);
              setResumed(true);
              setFootSignal((n) => n + 1);
              onProposalsFiled();
            } else {
              settleTries.current += 1;
            }
          }
        } catch { /* transient — next poll answers */ }
      }
    };
    void poll();
    const iv = setInterval(() => void poll(), 2000);
    return () => { live = false; clearInterval(iv); };
  }, [busy, watching, runId, engagementId, isPlanner, readThread, setBoth, onProposalsFiled]);

  const send = useCallback(async () => {
    const q = ask.trim();
    if (!q || busy) return;
    ownedTurn.current = true;
    setWatching(false);
    setBusy(true);
    setAskStartedAt(Date.now());
    setBoth((c) => [...c, { who: 'op', text: q }]);
    setAsk('');
    // The board travels with EVERY turn — a hold or a drag mid-conversation must not leave
    // the agent reasoning over a plan that is no longer there (FieldChat's live-form rule).
    // The header line also re-states role and tools each turn: conversationTurn silently
    // re-seeds an idle-released thread into a fresh generic session whose recap truncates the
    // first-turn framing away, so the framing a turn NEEDS must ride on the turn itself.
    const nowBlock = [
      '[Roadmap planning — the roadmap as it stands (record dates; “assumption pencilled” = the plan floats over them). Judge the PLAN as a whole; propose fixes with propose_update / propose_deliverable_item / propose_deliverable / propose_provisional / flag_open_question — proposals only, the operator holds:]',
      ...roadmapLines(dels),
    ];
    // The register's own scope rule (coverageReport): descoped and out-of-scope requirements
    // are not unfunded work — a hole named here must be one the plan actually has to fill.
    // Covered means named by a deliverable ON THE BOARD: the list route excludes dropped
    // deliverables, while coverage.noDeliverable would count a mapping to a dropped one as
    // cover — the exact requirement the G2 gate reports as uncovered.
    const coveredIds = new Set(dels.flatMap((d) => d.requirements.map((r) => r.id)));
    const uncovered = rows.filter((r) =>
      !coveredIds.has(r.id) && !r.merged_into_ref && r.status !== 'descoped' && r.type !== 'out_of_scope');
    const uncoveredLines = uncovered.slice(0, 40).map(
      (r) => `- ${r.ref} (${r.priority}) — ${squash(r.statement).slice(0, 140)}`,
    );
    if (uncovered.length > 40) uncoveredLines.push(`… and ${uncovered.length - 40} more`);
    const message = (runId === null || resumed
      ? [
          '[Roadmap planning — the deliverables plan as a whole]',
          'You are the roadmap PLANNER of this engagement. Your subject is the PLAN, not any single deliverable: whether the deliverables together cover every requirement, whether two of them carry the same work twice, whether the sequencing holds — nothing due before what it builds on, no missing dependency edge, no undated deliverable others build on — and what has been forgotten entirely.',
          ...nowBlock,
          ...(uncovered.length
            ? ['Requirements NO deliverable satisfies (in-scope only — descoped and out-of-scope are already excluded):', ...uncoveredLines]
            : ['Every in-scope requirement is satisfied by at least one deliverable.']),
          ...(openQuestions.length
            ? ['Open questions still standing:', ...openQuestions.slice(0, 15).map((qq) => `- ${qq.ref ?? `#${qq.id}`} — ${squash(qq.question).slice(0, 140)}`)]
            : []),
          'Judge in this order of harm: forgotten work · coverage holes · sequencing that cannot hold · overlap (say which deliverable should own the work) · deliverables whose content does not add up to their definition of done.',
          'Propose every fix as a proposal: propose_update amends a deliverable (dates, name, description, definition of done) or a content item; propose_deliverable_item adds content to an existing deliverable; propose_deliverable adds work nothing covers; propose_provisional marks a shaky standing with its reason; flag_open_question raises what you cannot decide. Every proposal lands in the review queue — and, where it names a deliverable already on the board, in that deliverable\'s own bench — you write nothing to the record; the operator holds every change, so give each proposal its reason.',
          'The full register is in register/*.md — read before you re-plan; ground every claim in what the requirements actually say.',
          '',
          `Operator: ${q}`,
        ]
      : [...nowBlock, '', `Operator: ${q}`]
    ).join('\n');
    try {
      const out = await agentApi.conversation(engagementId, message, runId, ROADMAP_CONTEXT);
      setResumed(false);
      setBoth((c) => [...c, { who: 'agent', text: out.reply, proposals: out.proposals }], Number(out.run.id));
      if (out.proposals.length) onProposalsFiled();
      void loadConvos();
    } catch (err) {
      setBoth((c) => [...c, { who: 'agent', text: err instanceof Error ? err.message : t('The agent did not answer.') }]);
    } finally {
      ownedTurn.current = false;
      setBusy(false);
    }
  }, [ask, busy, runId, resumed, dels, rows, openQuestions, engagementId, setBoth, loadConvos, onProposalsFiled, t]);

  /** Accept a pending proposal from the thread — the board re-reads everything it draws. */
  const holdProposal = useCallback(async (proposalId: number, kind: string) => {
    try {
      await agentApi.accept(proposalId, {});
      toast('ok', kind === 'provisional'
        ? t('#{id} held — the mark is set', { id: proposalId })
        : kind.startsWith('update_')
          ? t('#{id} held — the amendment is applied', { id: proposalId })
          : kind === 'create_deliverable_item'
            ? t('#{id} held — it landed in Contents', { id: proposalId })
            : t('#{id} held', { id: proposalId }));
      onChanged();
      // The card flips to its held state in place — the thread's copy stays honest.
      setBoth((c) => c.map((m) => (m.proposals?.some((p) => p.id === proposalId)
        ? { ...m, proposals: m.proposals.map((p) => (p.id === proposalId ? { ...p, status: 'accepted' } : p)) }
        : m)));
    } catch (err) { toast('err', err instanceof Error ? err.message : t('The hold was refused.')); }
  }, [onChanged, setBoth, toast, t]);

  /** The thread in the shared flyout's shape — every card names the deliverable it lands in. */
  const flyoutMsgs = useMemo<FlyoutMsg[]>(() => msgs.map((m) => ({
    who: m.who,
    text: m.text,
    extras: m.who === 'agent' && m.proposals?.length ? (
      <>
        {m.proposals.map((p) => {
          const kindStr = p.kind as unknown as string;
          const payload = p.payload as Record<string, unknown>;
          const landing = proposalDeliverableRef(kindStr, payload);
          return (
            <div className="rw-prop" key={p.id}>
              <span className="mark">◇</span>
              {landing ? (
                <span
                  className="src-ref rw-crossref"
                  title={t('This proposal lands in {ref} — hold it here, or treat it in that bench or the review queue', { ref: landing })}
                >→ {landing}</span>
              ) : null}
              <span className="t">{proposalSubject(kindStr, payload) || String(payload.title ?? payload.statement ?? `#${p.id}`)}</span>
              {HOLDABLE_IN_THREAD.has(kindStr) && p.status === 'pending' ? (
                <button type="button" className="lnkb" onClick={() => void holdProposal(p.id, p.kind)}>{t('Hold')}</button>
              ) : (
                // Only what still WAITS is "in the queue" — a held card must not contradict
                // the toast that just said it was applied.
                <span className="q">{p.status === 'pending' ? `${t(p.status)} · ${t('in the queue')}` : t(p.status)}</span>
              )}
            </div>
          );
        })}
      </>
    ) : undefined,
  })), [msgs, holdProposal, t]);

  return (
    <div
      className={`rw-planner${open ? '' : ' rw-planner--folded'}`}
      role="complementary"
      aria-label={t('Roadmap planner')}
    >
      <AgentFlyout
        open={open}
        onFold={setFold}
        label={t('Plan with the agent')}
        byline={t('agent · judges the whole plan, proposes into the queue')}
        sessionLabel={runId ? t('run #{id}', { id: runId }) : t('New conversation')}
        headExtra={
          <button
            type="button"
            className="rw-convsw"
            aria-expanded={convosOpen}
            onClick={() => { setConvosOpen((v) => !v); void loadConvos(); }}
          >
            <span className="u-label">{t('Conversations')}</span>
            <span className="chev" aria-hidden="true">▾</span>
          </button>
        }
        emptyState={
          <>
            <p className="rw-agent-note">
              {t('A conversation about the WHOLE roadmap: whether the deliverables cover every requirement, where they overlap, whether the sequencing holds, and what is missing entirely.')}
            </p>
            {/* P1, said once where it is read — every agent turn repeats it in its own byline. */}
            <p className="rw-agent-note">
              {t('The agent reads the plan and the register and proposes the fixes — every proposal lands in the review queue (and in its deliverable’s bench, where one exists), and nothing moves until you hold it.')}
            </p>
          </>
        }
        msgs={flyoutMsgs}
        busy={busy}
        activity={turnAct}
        busyStartedAt={askStartedAt}
        ask={ask}
        onAskChange={setAsk}
        onSend={() => void send()}
        placeholder={t('e.g. “Is every requirement covered, and does the sequencing hold?”')}
        footSignal={footSignal}
        overlay={convosOpen ? (
          <div className="rw-convpop" role="dialog" aria-label={t('Conversations')}>
            <div className="rw-convpop-head">
              <span className="u-label">{t('Planning conversations')}</span>
              <button type="button" className="x" title={t('Close the conversation list')} onClick={() => setConvosOpen(false)}>×</button>
            </div>
            <button type="button" className="rw-convnew" onClick={newConversation}>{t('+ New conversation')}</button>
            <div className="rw-convcards">
              {convos === null ? <div className="rw-agent-note">{t('reading the conversations…')}</div>
                : convos.map((r) => (
                  <button
                    type="button"
                    className={`rw-convcard${runId === r.id ? ' on' : ''}`}
                    key={r.id}
                    onClick={() => void openConversation(r)}
                  >
                    <span className="top">
                      <span className="src-ref">#{r.id}</span>
                      <span className="d voice-figure">{fmtDate(String(r.created_at ?? '').slice(0, 10))}</span>
                    </span>
                    <span className="t">{operatorWords((r.prompt ?? '').replace(/\s+/g, ' ')).slice(0, 90) || t('conversation')}</span>
                  </button>
                ))}
              {convos !== null && convos.length === 0 ? (
                <div className="rw-agent-note">{t('No conversations here yet.')}</div>
              ) : null}
            </div>
          </div>
        ) : null}
      />
    </div>
  );
}

export default PlannerChat;
